import {
	createAsyncThunk,
	createSlice,
} from "@reduxjs/toolkit";
import { IThunkApiExtra, RootState } from "../store";
import { STATUS } from "../globalContext/globalContext.interfaces";
import { ISignupContext, ISignupRequest, IGuestSignupRequest } from "../globalContext/signup.interface";
import { apiSignupService } from "src/api/signup/signup.service";

type SignupState = ISignupContext
type SignupRequestResponseDto = {statusCode: number, body :{ success: boolean, token: string }, data: { message: string }}
// Estado inicial para el signup
export const initialState: SignupState = {
	status: STATUS.init,
	message: '',
	apiStatusCode: null,
	data: '',
	token: undefined,
};

// Thunk para manejar el registro
export const signUp = createAsyncThunk<
  SignupState,
  ISignupRequest,
  { rejectValue: { status: number; message: string } }
>(
	'signup/signup',
	async (request: ISignupRequest | undefined, thunkApi) => {
		try {
			const response : SignupRequestResponseDto = await apiSignupService.signUp(request);
			const createdOK = response.statusCode === 201;
			const requireEmailValidation = response.statusCode === 200;

			if(requireEmailValidation){
				return {
					status: STATUS.idle,
					message: 'Requiere confirmación de correo',
					apiStatusCode: response.statusCode,
					token: response.body.token
				};
			}
			else if (createdOK) {
				return {
					status: STATUS.idle,
					message: 'Registro exitoso',
					apiStatusCode: response.statusCode,
				};
			} else {
				return thunkApi.rejectWithValue({
					status: response.statusCode,
					message: response.data.message || 'Error desconocido',
				});
			}
		} catch (error: any) {
			return thunkApi.rejectWithValue({
				status: error.status || 500,
				message: error.data?.message || error.message || 'Error en el registro',
			});
		}
	}
);

// Thunk para manejar el registro como invitado
export const signUpGuest = createAsyncThunk<
  SignupState,
  IGuestSignupRequest,
  { rejectValue: { status: number; message: string } }
>(
	'signup/signupGuest',
	async (request: IGuestSignupRequest | undefined, thunkApi) => {
		try {
			const response = await apiSignupService.signUpGuest(request);
			if (response.statusCode === 201) {
				return {
					status: STATUS.idle,
					message: 'Registro como invitado exitoso',
					apiStatusCode: response.statusCode,
					data: response.body?.data
				};
			} else {
				return thunkApi.rejectWithValue({
					status: response.statusCode,
					message: response.data.message || 'Error desconocido',
				});
			}
		} catch (error: any) {
			return thunkApi.rejectWithValue({
				status: error.status || 500,
				message: error.data?.message || error.message || 'Error en el registro como invitado',
			});
		}
	}
);

// Slice para signup
export const SignupSlice = createSlice({
	name: 'signup',
	initialState,
	reducers: {
		resetApiStatus: (state) => {
			state.status = STATUS.init;
			state.message = '';
			state.apiStatusCode = null;
		},
	},
	extraReducers: builder => {
		builder.addCase(signUp.pending, (state) => {
			state.status = STATUS.pending;
			state.apiStatusCode = null;
		});
		builder.addCase(signUp.fulfilled, (state, action) => {
			state.status = STATUS.idle;
			state.apiStatusCode = action.payload.apiStatusCode;
			state.token = action.payload.token;
		});
		builder.addCase(signUp.rejected, (state, action) => {
			state.status = STATUS.error;
			state.apiStatusCode = action.payload?.status || 500;
		});
		builder.addCase(signUpGuest.pending, (state) => {
			state.status = STATUS.pending;
			state.apiStatusCode = null;
		});
		builder.addCase(signUpGuest.fulfilled, (state, action) => {
			state.status = STATUS.idle;
			state.apiStatusCode = action.payload.apiStatusCode;
			state.data = action.payload.data;
		});
		builder.addCase(signUpGuest.rejected, (state, action) => {
			state.status = STATUS.error;
			state.apiStatusCode = action.payload?.status || 500;
		});
	}
});

export const { resetApiStatus } = SignupSlice.actions;

export const selectSignup = (state: RootState) => state.SignupState;
export const selectStatus = (state: RootState) => state.SignupState.status;
export const selectApiStatusCode = (state: RootState) => state.SignupState.apiStatusCode;
export const selectData = (state: RootState) => state.SignupState.data;